import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Search, Ban, RotateCcw, Repeat, RefreshCcw, Loader2, Plus, X, Eye } from "lucide-react";

const typeMeta = {
  cancelled: { label: "Cancelled", cls: "bg-red-100 text-red-700 border-red-200" },
  refund: { label: "Refund", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  exchange: { label: "Exchange", cls: "bg-blue-100 text-blue-700 border-blue-200" },
  replacement: { label: "Replacement", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
};

const tabs = [
  { v: "all", l: "All" },
  { v: "cancelled", l: "Cancelled" },
  { v: "refund", l: "Refunds" },
  { v: "exchange", l: "Exchanges" },
  { v: "replacement", l: "Replacements" },
];

function QtyRow({ item, qty, onChange }) {
  const k = item.medicine_id + "_" + item.unit;
  return (
    <div className="flex items-center justify-between gap-3 py-2 border-b border-slate-100 last:border-0">
      <div className="min-w-0">
        <div className="text-sm font-semibold truncate">{item.name}</div>
        <div className="text-xs text-slate-500">{item.unit} • sold {item.quantity} • ₹{Number(item.unit_price).toFixed(2)}</div>
      </div>
      <Input
        data-testid={`returns-qty-${k}`}
        type="number" min="0" max={item.quantity}
        value={qty[k] ?? ""}
        onChange={(e) => onChange(k, Math.min(Number(e.target.value) || 0, item.quantity))}
        className="h-9 w-20 font-mono-num" placeholder="0"
      />
    </div>
  );
}

export default function Returns() {
  const [records, setRecords] = useState([]);
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");
  const [matches, setMatches] = useState([]);
  const [inv, setInv] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [qty, setQty] = useState({});
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [medQ, setMedQ] = useState("");
  const [medMatches, setMedMatches] = useState([]);
  const [newItems, setNewItems] = useState([]);
  const [month, setMonth] = useState("");
  const [detail, setDetail] = useState(null);

  const loadRecords = async (t = tab, m = month) => {
    try {
      const params = {};
      if (t !== "all") params.type = t;
      if (m) params.month = m;
      const { data } = await api.get("/returns", { params });
      setRecords(data);
    } catch {
      setRecords([]);
    }
  };

  useEffect(() => { loadRecords(tab, month); /* eslint-disable-next-line */ }, [tab, month]);

  useEffect(() => {
    if (!q.trim()) return setMatches([]);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/invoices", { params: { q } });
        setMatches(data.slice(0, 6));
      } catch { setMatches([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    if (!medQ.trim()) return setMedMatches([]);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/medicines", { params: { q: medQ } });
        setMedMatches(data.slice(0, 6));
      } catch { setMedMatches([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [medQ]);

  const pickInvoice = async (m) => {
    try {
      const { data } = await api.get(`/invoices/${m.id}`);
      if (["cancelled", "returned"].includes(data.status)) {
        return toast.error(`Invoice already ${data.status}`);
      }
      setInv(data);
      setMatches([]);
      setQ("");
    } catch {
      toast.error("Could not load invoice");
    }
  };

  const openDialog = (d) => {
    setDialog(d);
    setQty({});
    setReason("");
    setNewItems([]);
    setMedQ("");
    setMedMatches([]);
  };

  const setQtyKey = (k, v) => setQty((prev) => ({ ...prev, [k]: v }));

  const selectedItems = () =>
    (inv?.items || [])
      .filter((it) => (Number(qty[it.medicine_id + "_" + it.unit]) || 0) > 0)
      .map((it) => ({
        medicine_id: it.medicine_id, name: it.name, unit: it.unit,
        quantity: Number(qty[it.medicine_id + "_" + it.unit]), pack_size: it.pack_size,
        unit_price: it.unit_price, gst_percent: it.gst_percent,
      }));

  const retValue = selectedItems().reduce((s, it) => s + it.unit_price * it.quantity * (1 + (it.gst_percent || 0) / 100), 0);
  const newValue = newItems.reduce((s, it) => s + it.price * it.quantity * (1 + (it.gst || 0) / 100), 0);
  const diff = newValue - retValue;

  const addNewItem = (m) => {
    setNewItems((prev) => {
      const ex = prev.find((p) => p.id === m.id);
      if (ex) return prev.map((p) => (p === ex ? { ...p, quantity: p.quantity + 1 } : p));
      return [...prev, { id: m.id, name: m.name, unit: "strip", quantity: 1, price: Number(m.strip_mrp) || 0, gst: m.gst_percent ?? 12 }];
    });
    setMedQ("");
    setMedMatches([]);
  };

  const submit = async () => {
    setBusy(true);
    try {
      if (dialog === "cancel") {
        const { data } = await api.post(`/invoices/${inv.id}/cancel`, { reason });
        toast.success(`Bill cancelled — ${data.return_no}`);
      } else if (dialog === "refund") {
        const items = selectedItems();
        if (!items.length) { toast.error("Enter a quantity to refund"); setBusy(false); return; }
        const { data } = await api.post(`/invoices/${inv.id}/return`, { items, reason });
        toast.success(`Refund ${data.return_no} — ₹${data.refund_total}`);
      } else if (dialog === "replace") {
        const items = selectedItems();
        if (!items.length) { toast.error("Enter a quantity to replace"); setBusy(false); return; }
        const { data } = await api.post(`/invoices/${inv.id}/replace`, { items, reason });
        toast.success(`Replacement ${data.return_no} recorded`);
      } else if (dialog === "exchange") {
        const items = selectedItems();
        if (!items.length || !newItems.length) { toast.error("Select return items and new items"); setBusy(false); return; }
        const { data } = await api.post(`/invoices/${inv.id}/exchange`, {
          return_items: items,
          new_items: newItems.map((n) => ({ medicine_id: n.id, unit: n.unit, quantity: n.quantity })),
          reason,
        });
        toast.success(`Exchange ${data.return_no} — difference ₹${data.difference}`);
      }
      setDialog(null);
      setInv(null);
      loadRecords();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="returns-page">
      <div>
        <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900">Returns &amp; Refunds</h1>
        <p className="text-sm text-slate-500">Cancel bills, process refunds, exchanges and replacements — sabhi adjustments ek jagah.</p>
      </div>

      <Card className="p-4 border-slate-200 space-y-3">
        <Label className="text-xs uppercase tracking-wide text-slate-500">Find invoice to take action</Label>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="returns-invoice-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by invoice no, customer, phone…" className="pl-9" />
        </div>
        {matches.length > 0 && (
          <div className="divide-y divide-slate-100 border border-slate-200 rounded-lg overflow-hidden">
            {matches.map((m) => {
              const done = ["cancelled", "returned"].includes(m.status);
              return (
                <button key={m.id} data-testid={`returns-match-${m.id}`} onClick={() => pickInvoice(m)} className={`w-full text-left px-4 py-2.5 flex items-center justify-between gap-3 ${done ? "opacity-60 bg-slate-50" : "hover:bg-emerald-50"}`}>
                  <div className="min-w-0">
                    <span className="font-mono-num font-bold text-sm">{m.invoice_no}</span>
                    <span className="text-xs text-slate-500 ml-2">{m.customer_name}</span>
                    {done && <Badge className={`ml-2 text-[10px] uppercase ${m.status === "cancelled" ? "bg-red-100 text-red-700 hover:bg-red-100" : "bg-amber-100 text-amber-700 hover:bg-amber-100"}`}>{m.status}</Badge>}
                  </div>
                  <span className="font-mono-num text-sm text-emerald-900 font-semibold shrink-0">₹{Number(m.grand_total).toFixed(2)}</span>
                </button>
              );
            })}
          </div>
        )}

        {inv && (
          <div className="border border-emerald-200 bg-emerald-50/50 rounded-lg p-4 space-y-3" data-testid="returns-active-invoice">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-mono-num font-bold">{inv.invoice_no}</div>
                <div className="text-xs text-slate-500">{inv.customer_name} • {new Date(inv.created_at).toLocaleString("en-IN")} • ₹{Number(inv.grand_total).toFixed(2)}</div>
              </div>
              <Button size="sm" variant="ghost" onClick={() => setInv(null)} data-testid="returns-clear-invoice"><X className="w-4 h-4" /></Button>
            </div>
            <div className="text-xs text-slate-600">
              {inv.items.map((it) => `${it.name} × ${it.quantity} ${it.unit}`).join(", ")}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <Button data-testid="action-cancel-btn" onClick={() => openDialog("cancel")} className="bg-red-600 hover:bg-red-700 text-white"><Ban className="w-4 h-4 mr-1.5" /> Cancel Bill</Button>
              <Button data-testid="action-refund-btn" onClick={() => openDialog("refund")} className="bg-amber-500 hover:bg-amber-600 text-white"><RotateCcw className="w-4 h-4 mr-1.5" /> Refund</Button>
              <Button data-testid="action-exchange-btn" onClick={() => openDialog("exchange")} className="bg-blue-600 hover:bg-blue-700 text-white"><Repeat className="w-4 h-4 mr-1.5" /> Exchange</Button>
              <Button data-testid="action-replace-btn" onClick={() => openDialog("replace")} className="bg-emerald-700 hover:bg-emerald-800 text-white"><RefreshCcw className="w-4 h-4 mr-1.5" /> Replace</Button>
            </div>
          </div>
        )}
      </Card>

      <Card className="border-slate-200">
        <div className="p-4 border-b border-slate-100 flex flex-wrap items-center gap-2" data-testid="returns-tabs">
          {tabs.map((t) => (
            <button key={t.v} data-testid={`returns-tab-${t.v}`} onClick={() => setTab(t.v)} className={`px-3.5 h-9 rounded-full text-sm font-medium transition-colors ${tab === t.v ? "bg-emerald-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{t.l}</button>
          ))}
          <div className="ml-auto flex items-center gap-2">
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="returns-month-filter" className="h-9 w-44" />
            {month && <Button size="sm" variant="ghost" onClick={() => setMonth("")} data-testid="returns-month-clear">Clear</Button>}
          </div>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Ref No</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Invoice</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Items</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>Date</TableHead>
                <TableHead className="text-right">View</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {records.map((r) => {
                const meta = typeMeta[r.type] || typeMeta.refund;
                const amount = r.type === "exchange" ? (r.difference ?? 0) : (r.refund_total ?? 0);
                return (
                  <TableRow key={r.id} data-testid={`return-record-${r.id}`}>
                    <TableCell className="font-mono-num font-bold text-sm">{r.return_no}</TableCell>
                    <TableCell><Badge className={`${meta.cls} border text-xs`} data-testid={`return-type-${r.id}`}>{meta.label}</Badge></TableCell>
                    <TableCell className="font-mono-num text-sm">{r.invoice_no}</TableCell>
                    <TableCell className="text-sm">{r.customer_name || "—"}</TableCell>
                    <TableCell className="text-xs text-slate-500 max-w-[220px] truncate" title={(r.items || []).map((i) => `${i.name} × ${i.quantity}`).join(", ")}>
                      {(r.items || []).map((i) => i.name).join(", ")}
                    </TableCell>
                    <TableCell className="text-right font-mono-num font-semibold">
                      {r.type === "replacement" ? "—" : `₹${Math.abs(amount).toFixed(2)}`}
                      {r.type === "exchange" && amount !== 0 && (
                        <span className={`block text-[10px] ${amount > 0 ? "text-emerald-700" : "text-red-600"}`}>
                          {amount > 0 ? "customer paid" : "refunded"}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-slate-500 max-w-[160px] truncate">{r.reason || "—"}</TableCell>
                    <TableCell className="text-xs text-slate-500">{new Date(r.created_at).toLocaleString("en-IN")}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" data-testid={`return-view-${r.id}`} onClick={() => setDetail(r)}><Eye className="w-4 h-4 mr-1" /> Open</Button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {records.length === 0 && (
                <TableRow><TableCell colSpan={9} className="text-center py-10 text-slate-400">No records in this category yet</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={dialog === "refund" || dialog === "replace"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-w-md bg-white" data-testid="returns-qty-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">
              {dialog === "refund" ? "Refund items" : "Replace items"} — {inv?.invoice_no}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[50vh] overflow-y-auto">
            {inv?.items.map((it) => <QtyRow key={it.medicine_id + "_" + it.unit} item={it} qty={qty} onChange={setQtyKey} />)}
            <div>
              <Label className="text-xs">Reason</Label>
              <Input data-testid="returns-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1 h-10" placeholder={dialog === "replace" ? "e.g. damaged strip, expired" : "Optional"} />
            </div>
            {dialog === "refund" && retValue > 0 && (
              <div className="text-sm font-semibold text-amber-700" data-testid="refund-preview">Refund amount: ₹{retValue.toFixed(2)}</div>
            )}
            {dialog === "replace" && (
              <p className="text-xs text-slate-500">Same medicine ka naya piece diya jayega — stock se fresh piece niklega, damaged piece wapas stock mein nahi jayega. Koi payment change nahi.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Close</Button>
            <Button onClick={submit} disabled={busy} data-testid="returns-confirm-btn" className={dialog === "refund" ? "bg-amber-500 hover:bg-amber-600 text-white" : "bg-emerald-700 hover:bg-emerald-800 text-white"}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : dialog === "refund" ? "Process Refund" : "Confirm Replacement"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog === "cancel"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-w-md bg-white" data-testid="returns-cancel-dialog">
          <DialogHeader><DialogTitle className="font-display">Cancel bill {inv?.invoice_no}?</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Full bill of <span className="font-bold">₹{Number(inv?.grand_total || 0).toFixed(2)}</span> will be cancelled.
              Stock wapas inventory mein chala jayega aur ye sale reports se hat jayegi. Invoice list mein "Cancelled" badge dikhega.
            </p>
            <div>
              <Label className="text-xs">Reason</Label>
              <Input data-testid="cancel-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1 h-10" placeholder="e.g. wrong billing, customer changed mind" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Keep Bill</Button>
            <Button onClick={submit} disabled={busy} data-testid="cancel-confirm-btn" className="bg-red-600 hover:bg-red-700 text-white">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Yes, Cancel Bill"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog === "exchange"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-w-2xl bg-white" data-testid="returns-exchange-dialog">
          <DialogHeader><DialogTitle className="font-display">Exchange — {inv?.invoice_no}</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4 max-h-[45vh] overflow-y-auto pr-1">
            <div>
              <div className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">Customer returns</div>
              {inv?.items.map((it) => <QtyRow key={it.medicine_id + "_" + it.unit} item={it} qty={qty} onChange={setQtyKey} />)}
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">New items given</div>
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input data-testid="exchange-med-search" value={medQ} onChange={(e) => setMedQ(e.target.value)} placeholder="Search medicine…" className="pl-8 h-9" />
              </div>
              {medMatches.length > 0 && (
                <div className="mt-1 border border-slate-200 rounded-lg divide-y divide-slate-100 overflow-hidden">
                  {medMatches.map((m) => (
                    <button key={m.id} data-testid={`exchange-med-${m.id}`} onClick={() => addNewItem(m)} className="w-full text-left px-3 py-2 hover:bg-blue-50 flex justify-between gap-2">
                      <span className="text-sm truncate">{m.name}</span>
                      <span className="text-xs text-slate-500 shrink-0">₹{Number(m.strip_mrp).toFixed(2)}/strip</span>
                    </button>
                  ))}
                </div>
              )}
              <div className="mt-2 space-y-2">
                {newItems.map((n) => (
                  <div key={n.id} className="flex items-center justify-between gap-2 border border-slate-100 rounded-lg px-3 py-2">
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{n.name}</div>
                      <div className="text-xs text-slate-500">₹{n.price.toFixed(2)} / strip</div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => setNewItems(newItems.map((x) => (x === n ? { ...x, quantity: Math.max(1, x.quantity - 1) } : x)))}>-</Button>
                      <span className="w-6 text-center font-mono-num text-sm">{n.quantity}</span>
                      <Button size="sm" variant="outline" className="h-7 w-7 p-0" data-testid={`exchange-plus-${n.id}`} onClick={() => setNewItems(newItems.map((x) => (x === n ? { ...x, quantity: x.quantity + 1 } : x)))}><Plus className="w-3.5 h-3.5" /></Button>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => setNewItems(newItems.filter((x) => x !== n))}><X className="w-3.5 h-3.5" /></Button>
                    </div>
                  </div>
                ))}
                {newItems.length === 0 && <div className="text-xs text-slate-400 py-3 text-center">Search and add new medicines</div>}
              </div>
            </div>
          </div>
          <div className="border-t border-slate-100 pt-3 space-y-1.5 text-sm">
            <div className="flex justify-between"><span className="text-slate-500">Return value</span><span className="font-mono-num">₹{retValue.toFixed(2)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">New items value</span><span className="font-mono-num">₹{newValue.toFixed(2)}</span></div>
            <div className="flex justify-between font-bold" data-testid="exchange-difference">
              <span>{diff >= 0 ? "Customer pays extra" : "Refund to customer"}</span>
              <span className={diff >= 0 ? "text-emerald-700" : "text-red-600"}>₹{Math.abs(diff).toFixed(2)}</span>
            </div>
            <div>
              <Label className="text-xs">Reason</Label>
              <Input data-testid="exchange-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1 h-9" placeholder="Optional" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Close</Button>
            <Button onClick={submit} disabled={busy} data-testid="exchange-confirm-btn" className="bg-blue-600 hover:bg-blue-700 text-white">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Process Exchange"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg bg-white" data-testid="return-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              {detail?.return_no}
              {detail && <Badge className={`${(typeMeta[detail.type] || typeMeta.refund).cls} border text-xs`} data-testid="return-detail-type">{(typeMeta[detail.type] || typeMeta.refund).label}</Badge>}
            </DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto">
              <div className="text-sm text-slate-600 space-y-0.5">
                <div>Invoice: <span className="font-mono-num font-semibold">{detail.invoice_no}</span></div>
                <div>Customer: {detail.customer_name || "Walk-in"}{detail.customer_phone ? ` • ${detail.customer_phone}` : ""}</div>
                <div>Date: {new Date(detail.created_at).toLocaleString("en-IN")}</div>
                {detail.reason && <div>Reason: {detail.reason}</div>}
              </div>

              <div>
                <div className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">
                  {detail.type === "exchange" ? "Items returned by customer" : detail.type === "replacement" ? "Replaced items" : detail.type === "cancelled" ? "Cancelled items" : "Refunded items"}
                </div>
                <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
                  {(detail.items || []).map((i, idx) => (
                    <div key={idx} className="px-3 py-2 flex items-center justify-between gap-3" data-testid={`detail-item-${idx}`}>
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{i.name}</div>
                        <div className="text-xs text-slate-500">{i.quantity} × {i.unit} @ ₹{Number(i.unit_price).toFixed(2)}</div>
                      </div>
                      <div className="font-mono-num text-sm font-semibold shrink-0">₹{Number(i.total || 0).toFixed(2)}</div>
                    </div>
                  ))}
                </div>
              </div>

              {detail.type === "exchange" && (detail.new_items || []).length > 0 && (
                <div>
                  <div className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">New items given</div>
                  <div className="border border-blue-100 rounded-lg divide-y divide-slate-100">
                    {detail.new_items.map((i, idx) => (
                      <div key={idx} className="px-3 py-2 flex items-center justify-between gap-3" data-testid={`detail-new-item-${idx}`}>
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate">{i.name}</div>
                          <div className="text-xs text-slate-500">{i.quantity} × {i.unit} @ ₹{Number(i.unit_price).toFixed(2)}</div>
                        </div>
                        <div className="font-mono-num text-sm font-semibold shrink-0">₹{Number(i.total || 0).toFixed(2)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="border-t border-slate-100 pt-3 space-y-1 text-sm">
                {detail.type === "exchange" ? (
                  <>
                    <div className="flex justify-between"><span className="text-slate-500">Return value</span><span className="font-mono-num">₹{Number(detail.returned_value || 0).toFixed(2)}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">New items value</span><span className="font-mono-num">₹{Number(detail.new_value || 0).toFixed(2)}</span></div>
                    <div className="flex justify-between font-bold" data-testid="detail-difference">
                      <span>{(detail.difference ?? 0) >= 0 ? "Customer paid extra" : "Refunded to customer"}</span>
                      <span className={(detail.difference ?? 0) >= 0 ? "text-emerald-700" : "text-red-600"}>₹{Math.abs(detail.difference ?? 0).toFixed(2)}</span>
                    </div>
                  </>
                ) : detail.type === "replacement" ? (
                  <div className="text-xs text-slate-500">No payment change — replacement only.</div>
                ) : (
                  <div className="flex justify-between font-bold" data-testid="detail-total">
                    <span>{detail.type === "cancelled" ? "Cancelled bill value" : "Refund total"}</span>
                    <span className="text-red-600">₹{Number(detail.refund_total || 0).toFixed(2)}</span>
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetail(null)} data-testid="return-detail-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
